import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Loader2, X, Check, AlertTriangle, FileText, StickyNote, Sparkles, ArrowDownLeft, CreditCard,
    ChevronRight, ListChecks, Search, Link2, Tag, Wand2, FilePlus, Ban, RotateCcw,
} from 'lucide-react';
import { ACCENT, OK, WARN, BAD, VIOLET, tableSurfaces } from '../lib/uiTokens';
import Dropdown from '../components/receptura/Dropdown';
import ModulLayout from '../components/layout/ModulLayout';
import ObdobiNavigator from './finance/ObdobiNavigator';
import PohybyListPanel, { lzeVybrat } from './banka/PohybyListPanel';
import PohybDetail from './banka/PohybDetail';
import BankaStatistikyPanel from './banka/BankaStatistikyPanel';
import { sestavNalezy } from './banka/bankaNalezy';
import ModalShell from '../components/ui/ModalShell';
import {
    fmt, fmtDate, zobrazProtistranu, maskaKarty, KATEGORIE, KAT_LABEL, STAV_LABEL,
    POHLEDY, CASTKA_PASMA, token, postJson,
} from './banka/bankaSdilene';
import { MESICE_PLNE } from '../lib/formatters';
import { stylySpizirny } from '../lib/spizirnaUi';

// Částka pohybu — příchozí zeleně (+), odchozí červeně (−), tučně, tabulárně.
// Zůstává tady, protože ji potřebují modaly níž.
const Castka = ({ smer, castka, size = '1.05rem' }) => (
    <span className="tabular-nums" style={{ color: smer === 'prichozi' ? OK : BAD, fontWeight: 700, fontSize: size, whiteSpace: 'nowrap' }}>
        {smer === 'prichozi' ? '+' : '−'}{fmt(castka)}
    </span>
);

const VYCHOZI_FILTR = { smer: [], castka: [], tag: '' };

/**
 * Finance · Banka — import ABO/GPC výpisu a párování pohybů na doklady.
 * Jedna platba může nést víc dokladů (alokace v párovacím modalu).
 *
 * **Třísloupcový standard od 3.481** (`standard-rozvrzeni.md`):
 *
 *     seznam pohybů  |  detail pohybu  |  statistiky účtu + „K dořešení"
 *
 * Co se tím narovnalo proti předchozí podobě:
 *  - rozvržení dělá `ModulLayout`, ne vlastní `flex-col xl:flex-row` s vlastní
 *    šířkou panelu `clamp(340,25vw,480)`; panel je teď normových 320 px
 *  - hlavičku seznamu dělá `ListToolbar` — hledání vlevo, akce vpravo, pohledy
 *    pod tím. Dřív byly akce vlevo vedle výběru účtu a hledání až v druhém
 *    bloku vpravo, tedy přesně naopak, než norma říká
 *  - devět pilulek v jedné řadě (šest stavů + tři směry) nahradil `PohledOvladac`
 *    s pohledy jako daty. Směr a částka odešly do `ListFiltr` — je to osa, kterou
 *    kombinuješ s pohledem, ne pohled sám
 *  - **pohyb má detail.** Dřív nebylo kam kliknout: párování bylo v modalu,
 *    zařazení v rozbalovacím menu přímo v řádku. To menu se muselo renderovat
 *    přes portál a šedesáti řádky kódu dopočítávat, aby se neořízlo o okraj okna
 *  - období řeší `ObdobiNavigator` jako zbytek Financí, ne dvě datumová pole
 *
 * **Panel se kreslí i bez výběru** (`panelVzdy`). Zůstatek a „K dořešení" jsou
 * vlastnost účtu, ne vybraného pohybu — nad seznamem by jen ukrajovaly výšku.
 */
export default function FinanceBanka({
    cardStyle, isDark, isMobile = false, onOpenDoklad,
    rok, mesic, setRok, setMesic, rezervaDole = null,
}) {
    const [ucty, setUcty] = useState([]);
    const [ucetId, setUcetId] = useState(null);
    const [transakce, setTransakce] = useState([]);
    const [staty, setStaty] = useState(null);
    const [loading, setLoading] = useState(true);

    const [pohled, setPohled] = useState('vse');
    const [filtrHodnoty, setFiltrHodnoty] = useState(VYCHOZI_FILTR);
    const [hledej, setHledej] = useState('');
    const [vybranyId, setVybranyId] = useState(null);

    const [uploadInfo, setUploadInfo] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [navrhyBusy, setNavrhyBusy] = useState(false);
    const [parujiKarty, setParujiKarty] = useState(false);
    const [parujici, setParujici] = useState(null);
    const [zarazujici, setZarazujici] = useState(null);
    const [dokladProPlatbu, setDokladProPlatbu] = useState(null);
    const [navrhy, setNavrhy] = useState(null);
    const [pravidlaOpen, setPravidlaOpen] = useState(false);
    const [kDoreseniOpen, setKDoreseniOpen] = useState(false);
    // Hromadný výběr — režim zapíná „Vybrat více" v hlavičce seznamu.
    const [vyberRezim, setVyberRezim] = useState(false);
    const [vybrane, setVybrane] = useState(() => new Set());
    const [hromadneZarazeni, setHromadneZarazeni] = useState(false);
    const [hromadneBusy, setHromadneBusy] = useState(false);
    const fileRef = useRef(null);

    // Tentýž balík stylů jako Pokladna a Suroviny — pole hledání potřebuje
    // `width: 100%`, jinak ho `ListToolbar` sice roztáhne, ale input si uvnitř
    // drží svoji vlastní šířku a placeholder se ořízne.
    const { mutedColor, inputStyle } = useMemo(() => stylySpizirny(isDark, ACCENT), [isDark]);

    // Období z navigátoru modulu — měsíc jako Doklady, DPH i Výsledovka.
    const { od, doDatum } = useMemo(() => {
        const dvoji = (n) => String(n).padStart(2, '0');
        const posledni = new Date(rok, mesic, 0).getDate();
        return { od: `${rok}-${dvoji(mesic)}-01`, doDatum: `${rok}-${dvoji(mesic)}-${dvoji(posledni)}` };
    }, [rok, mesic]);

    const nactiUcty = useCallback(() => {
        fetch('/api/finance/banka/ucty').then(r => r.json()).then(setUcty).catch(() => {});
    }, []);

    /** Parametry seznamu — pohled, filtr a hledání dohromady. */
    const parametry = useCallback(() => {
        const p = new URLSearchParams();
        if (ucetId) p.set('ucet_id', ucetId);
        p.set('od', od);
        p.set('do', doDatum);

        const pv = POHLEDY.find(x => x.id === pohled);
        if (pv?.stav) p.set('stav', pv.stav);
        if (pv?.diagnostika) p.set('diagnostika', pv.diagnostika);

        // Směr je jednohodnotový — dvě zaškrtnutá pásma znamenají „oboje",
        // tedy nefiltrovat.
        if (filtrHodnoty.smer?.length === 1) p.set('smer', filtrHodnoty.smer[0]);
        if (filtrHodnoty.tag) p.set('tag', filtrHodnoty.tag);

        // Pásma částky se slučují do jedné obálky min–max.
        const pasma = (filtrHodnoty.castka || []).map(k => CASTKA_PASMA[k]).filter(Boolean);
        if (pasma.length) {
            const miny = pasma.map(x => x.min).filter(x => x != null);
            const maxy = pasma.map(x => x.max);
            if (miny.length === pasma.length) p.set('castka_min', String(Math.min(...miny)));
            if (!maxy.includes(null)) p.set('castka_max', String(Math.max(...maxy)));
        }

        if (hledej) p.set('q', hledej);
        return p;
    }, [ucetId, od, doDatum, pohled, filtrHodnoty, hledej]);

    const nactiTransakce = useCallback(() => {
        setLoading(true);
        fetch('/api/finance/banka/transakce?' + parametry().toString())
            .then(r => r.json())
            .then(d => { setTransakce(Array.isArray(d) ? d : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, [parametry]);

    // Statistiky respektují účet + období, ne pohled a filtr — ať je vidět
    // celkový obraz účtu bez ohledu na to, co je zrovna v seznamu.
    const nactiStatistiky = useCallback(() => {
        const p = new URLSearchParams();
        if (ucetId) p.set('ucet_id', ucetId);
        p.set('od', od);
        p.set('do', doDatum);
        fetch('/api/finance/banka/statistiky?' + p.toString())
            .then(r => r.json()).then(setStaty).catch(() => {});
    }, [ucetId, od, doDatum]);

    useEffect(() => { nactiUcty(); }, [nactiUcty]);
    useEffect(() => {
        const id = setTimeout(nactiTransakce, hledej ? 300 : 0);
        return () => clearTimeout(id);
    }, [nactiTransakce, hledej]);
    useEffect(() => { nactiStatistiky(); }, [nactiStatistiky]);
    // Vybraný pohyb, který v nové sadě není, musí pustit detail — jinak by
    // sloupec ukazoval položku, která v seznamu vedle už není.
    useEffect(() => {
        if (vybranyId && !transakce.some(t => t.id === vybranyId)) setVybranyId(null);
    }, [transakce, vybranyId]);

    // Výběr drží jen pohyby, které jsou v seznamu vidět a jdou měnit — po
    // změně období/filtru nesmí hromadná akce sáhnout na něco schovaného.
    useEffect(() => {
        setVybrane(prev => {
            if (!prev.size) return prev;
            const zustava = new Set(transakce.filter(t => prev.has(t.id) && lzeVybrat(t)).map(t => t.id));
            return zustava.size === prev.size ? prev : zustava;
        });
    }, [transakce]);

    const vybrany = transakce.find(t => t.id === vybranyId) || null;
    const patchTransakce = (nt) => setTransakce(prev => prev.map(x => (x.id === nt.id ? nt : x)));
    const nastavFiltr = (id, hodnota) => setFiltrHodnoty(h => ({ ...h, [id]: hodnota }));
    const resetFiltru = () => setFiltrHodnoty(VYCHOZI_FILTR);

    const nahrajSoubor = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        setUploadInfo(null);
        // Nahrání po jednom (každý výpis si sám dohledá účet dle záhlaví), souhrn agregujeme.
        const s = { soubory: 0, nove: 0, duplicitni: 0, celkem: 0, auto_sparovano: 0, auto_zarazeno: 0, doplneno: 0, varovani: [], ucty: [], chyby: [] };
        for (const file of files) {
            try {
                const fd = new FormData();
                fd.append('soubor', file);
                if (ucetId) fd.append('ucet_id', ucetId);
                const r = await fetch('/api/finance/banka/vypis', {
                    method: 'POST', headers: { 'X-CSRF-TOKEN': token() }, body: fd,
                });
                const text = await r.text();
                let j = {};
                try { j = text ? JSON.parse(text) : {}; } catch { j = { error: `Chyba serveru (HTTP ${r.status})` }; }
                if (!r.ok) { s.chyby.push(`${file.name}: ${j.error || j.message || `HTTP ${r.status}`}`); }
                else {
                    s.soubory++;
                    s.nove += j.nove || 0;
                    s.duplicitni += j.duplicitni || 0;
                    s.celkem += j.celkem || 0;
                    s.auto_sparovano += j.auto_sparovano || 0;
                    s.auto_zarazeno += j.auto_zarazeno || 0;
                    s.doplneno += j.doplneno || 0;
                    // Varování parseru (neúplný výpis) — bez „neznámý typ věty", ten je šum.
                    (j.varovani || []).filter(v => !v.includes('neznámý typ věty')).forEach(v => s.varovani.push(v));
                    if (j.ucet?.nazev && !s.ucty.includes(j.ucet.nazev)) s.ucty.push(j.ucet.nazev);
                }
            } catch (err) { s.chyby.push(`${file.name}: ${err?.message || 'síťová chyba'}`); }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
        setUploadInfo({ souhrn: s });
        nactiUcty();
        nactiTransakce();
        nactiStatistiky();
        // Po importu automaticky spustit AI návrhy (jen když přibyly nové pohyby).
        if (s.nove > 0) projitPohyby();
    };

    const rozparuj = async (t) => {
        if (!confirm('Zrušit párování tohoto pohybu? Doklady se vrátí z „zaplaceno".')) return;
        const r = await postJson(`/api/finance/banka/transakce/${t.id}/rozparovat`);
        if (r.ok) { const { transakce: nt } = await r.json(); patchTransakce(nt); nactiStatistiky(); }
    };
    const ignoruj = async (t) => {
        if (!confirm('Označit pohyb jako nepárovatelný? Zmizí z fronty nespárovaných.')) return;
        const r = await postJson(`/api/finance/banka/transakce/${t.id}/zaradit`, { tag: 'osobni', stav: 'ignorovano' });
        if (r.ok) { const { transakce: nt } = await r.json(); patchTransakce(nt); nactiStatistiky(); }
    };
    const zarad = async (t, tag, poznamka) => {
        // Platba je buď spárovaná, nebo zařazená — zařazením se doklady odpojí.
        if (t.doklady?.length && !confirm(`Platba je spárovaná s ${t.doklady.length === 1 ? 'dokladem' : `${t.doklady.length} doklady`}. Zařazením na kategorii se doklady odpojí a vrátí do „čeká na platbu". Pokračovat?`)) return;
        const r = await postJson(`/api/finance/banka/transakce/${t.id}/zaradit`, { tag, poznamka });
        if (r.ok) { const { transakce: nt } = await r.json(); patchTransakce(nt); setZarazujici(null); nactiStatistiky(); }
    };

    // ── Hromadné akce ───────────────────────────────────────────────────────
    const prepniVyberRezim = () => {
        if (vyberRezim) { setVyberRezim(false); setVybrane(new Set()); return; }
        // Otevřený pohyb se stane prvním vybraným a detail se zavře, ať je
        // seznam přes celou šířku se sloupcem zaškrtnutí.
        setVybrane(vybrany && lzeVybrat(vybrany) ? new Set([vybrany.id]) : new Set());
        setVybranyId(null);
        setVyberRezim(true);
    };
    const oznac = (ids, zapnout) => setVybrane(prev => {
        const n = new Set(prev);
        ids.forEach(id => (zapnout ? n.add(id) : n.delete(id)));
        return n;
    });
    const vybraneTransakce = transakce.filter(t => vybrane.has(t.id));
    const vybratelne = transakce.filter(lzeVybrat);
    const vseVybrano = vybratelne.length > 0 && vybratelne.every(t => vybrane.has(t.id));
    // Opakované platby (OSSZ, leasing, poplatky…) poznáš podle protistrany —
    // dooznačí všechny viditelné pohyby se stejným protiúčtem nebo názvem.
    // U karty je „název" jen maska karty, ten by spojil všechny nákupy kartou.
    const klicePohybu = (t) => [
        t.protistrana_ucet ? `u:${t.protistrana_ucet}/${t.protistrana_banka || ''}` : null,
        !t.je_kartova && t.protistrana_nazev ? `n:${t.protistrana_nazev.trim().toLowerCase()}` : null,
    ].filter(Boolean);
    const podobne = (() => {
        const klice = new Set(vybraneTransakce.flatMap(klicePohybu));
        if (!klice.size) return [];
        return vybratelne.filter(t => !vybrane.has(t.id) && klicePohybu(t).some(k => klice.has(k)));
    })();

    const poHromadneAkci = (j) => {
        if (j?.preskoceno) alert(`${j.preskoceno} spárovaných pohybů přeskočeno — ty se mění jen v detailu.`);
        setVybrane(new Set());
        nactiTransakce();
        nactiStatistiky();
    };
    const zaraditVybrane = async (tag, poznamka) => {
        const polozky = [...vybrane].map(id => ({ transakce_id: id, tag }));
        const r = await postJson('/api/finance/banka/zaradit-hromadne', { polozky, poznamka: poznamka || undefined });
        if (r.ok) { setHromadneZarazeni(false); poHromadneAkci(await r.json()); }
        else alert('Zařazení se nepovedlo.');
    };
    const stavVybranych = async (akce) => {
        const n = vybrane.size;
        const otazka = akce === 'neparovatelne'
            ? `Označit ${n} pohybů jako nepárovatelné?`
            : `Vrátit ${n} pohybů do nespárovaných? Zařazení (kategorie) se u nich smaže.`;
        if (!confirm(otazka)) return;
        setHromadneBusy(true);
        try {
            const r = await postJson('/api/finance/banka/stav-hromadne', { ids: [...vybrane], akce });
            if (r.ok) poHromadneAkci(await r.json());
            else alert('Akce se nepovedla.');
        } finally { setHromadneBusy(false); }
    };
    const projitPohyby = async () => {
        setNavrhyBusy(true);
        try {
            const r = await postJson('/api/finance/banka/navrhy-kategorii', { ucet_id: ucetId || undefined });
            const j = await r.json();
            if (r.ok) setNavrhy({ items: j.navrhy || [], ai_pouzito: j.ai_pouzito, celkem: j.celkem_neparovano });
        } catch { /* ignore */ }
        setNavrhyBusy(false);
    };

    // Dodatečné párování jistých kartových shod — pro pohyby z doby před
    // automatikou. Stejné pravidlo jako při importu, vratné přes Rozpárovat.
    const sparujKartove = async () => {
        setParujiKarty(true);
        try {
            const r = await postJson('/api/finance/banka/sparuj-kartove', { ucet_id: ucetId || undefined });
            const j = await r.json();
            if (r.ok) {
                alert(j.sparovano
                    ? `Spárováno ${j.sparovano} plateb kartou.`
                    : 'Žádná další jistá kartová shoda — zbytek zůstává k ručnímu párování.');
                nactiTransakce();
                nactiStatistiky();
            }
        } catch { /* ignore */ }
        setParujiKarty(false);
    };

    const ucetLabel = ucetId
        ? (ucty.find(u => u.id === ucetId)?.nazev ?? null)
        : 'všechny aktivní účty';

    // Souhrn importu — krátká zpráva nad seznamem, dá se odklepnout.
    const souhrnImportu = uploadInfo?.souhrn && (
        <div className="rounded-xl px-3 py-2 mb-2.5 flex items-start gap-2 flex-shrink-0"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}>
            <Check size={15} style={{ color: OK, flexShrink: 0, marginTop: 2 }} />
            <div className="text-[12px] flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>
                Nahráno <b>{uploadInfo.souhrn.soubory}</b>{uploadInfo.souhrn.ucty.length ? ` (${uploadInfo.souhrn.ucty.join(', ')})` : ''}:
                {' '}nových <b>{uploadInfo.souhrn.nove}</b>, duplicitních <b>{uploadInfo.souhrn.duplicitni}</b>,
                {' '}auto-spárováno <b style={{ color: OK }}>{uploadInfo.souhrn.auto_sparovano}</b>
                {uploadInfo.souhrn.auto_zarazeno > 0 && <>, auto-zařazeno <b style={{ color: OK }}>{uploadInfo.souhrn.auto_zarazeno}</b></>}
                {uploadInfo.souhrn.doplneno > 0 && <>, doplněno ze starších importů <b>{uploadInfo.souhrn.doplneno}</b></>}
                {uploadInfo.souhrn.varovani.length > 0 && (
                    <span style={{ color: BAD }}> · {uploadInfo.souhrn.varovani.join(' · ')}</span>
                )}
                {uploadInfo.souhrn.chyby.length > 0 && (
                    <span style={{ color: BAD }}> · {uploadInfo.souhrn.chyby.join(' · ')}</span>
                )}
            </div>
            <button onClick={() => setUploadInfo(null)} aria-label="Zavřít" style={{ color: 'var(--text-tertiary)' }}><X size={15} /></button>
        </div>
    );

    // Lišta hromadných akcí pod seznamem — jen v režimu výběru.
    const soucetVybranych = vybraneTransakce.reduce((s, t) => s + (t.smer === 'prichozi' ? 1 : -1) * Number(t.castka || 0), 0);
    const tlHromadne = (aktivni = true, hlavni = false) => ({
        minHeight: isMobile ? 40 : 32,
        background: hlavni ? ACCENT : 'transparent',
        color: hlavni ? '#fff' : 'var(--text-primary)',
        border: hlavni ? 'none' : '1px solid var(--border-default)',
        opacity: aktivni ? 1 : 0.45,
        cursor: aktivni ? 'pointer' : 'default',
    });
    const nicNevybrano = vybrane.size === 0 || hromadneBusy;
    const hromadnaLista = vyberRezim && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5"
            style={{ borderTop: `2px solid ${ACCENT}`, background: 'var(--surface-elevated)' }}>
            <div className="text-[12.5px] mr-auto flex items-baseline gap-2 flex-wrap">
                {vybrane.size > 0 ? (
                    <>
                        <b>Vybráno {vybrane.size}</b>
                        <span className="tabular-nums" style={{ color: soucetVybranych >= 0 ? OK : BAD, fontWeight: 700 }}>
                            {soucetVybranych >= 0 ? '+' : '−'}{fmt(Math.abs(soucetVybranych))} Kč
                        </span>
                    </>
                ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>
                        Klikni na pohyby{isMobile ? '' : ' (Shift+klik = rozsah)'} — spárované vybrat nejde
                    </span>
                )}
            </div>
            <button type="button" className="text-[12px] px-2.5 rounded-lg" style={tlHromadne(vybratelne.length > 0)}
                disabled={!vybratelne.length}
                onClick={() => setVybrane(vseVybrano ? new Set() : new Set(vybratelne.map(t => t.id)))}>
                {vseVybrano ? 'Zrušit výběr' : `Vybrat vše (${vybratelne.length})`}
            </button>
            {podobne.length > 0 && (
                <button type="button" className="text-[12px] px-2.5 rounded-lg" style={tlHromadne()}
                    title="Dooznačit všechny viditelné pohyby se stejnou protistranou"
                    onClick={() => oznac(podobne.map(t => t.id), true)}>
                    + stejná protistrana ({podobne.length})
                </button>
            )}
            <button type="button" className="text-[12px] px-2.5 rounded-lg inline-flex items-center gap-1.5" style={tlHromadne(!nicNevybrano)}
                disabled={nicNevybrano} onClick={() => stavVybranych('vratit')}
                title="Zpět do nespárovaných, smaže zařazení">
                <RotateCcw size={13} /> Vrátit
            </button>
            <button type="button" className="text-[12px] px-2.5 rounded-lg inline-flex items-center gap-1.5" style={tlHromadne(!nicNevybrano)}
                disabled={nicNevybrano} onClick={() => stavVybranych('neparovatelne')}>
                <Ban size={13} /> Nepárovatelné
            </button>
            <button type="button" className="text-[12px] font-semibold px-3 rounded-lg inline-flex items-center gap-1.5" style={tlHromadne(!nicNevybrano, true)}
                disabled={nicNevybrano} onClick={() => setHromadneZarazeni(true)}>
                {hromadneBusy ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} Zařadit…
            </button>
        </div>
    );

    // Na mobilu třetí sloupec není (`mobilPanel={false}`) — „K dořešení" se
    // tam otevírá z řádku nad seznamem do modalu (norma: třetí sloupec na
    // mobilu → modal). Bez toho by se chybějící výpis na telefonu nikdo nedozvěděl.
    const pocetNalezu = sestavNalezy(staty).length;
    const kDoreseniRadek = isMobile && pocetNalezu > 0 && (
        <div className="flex-shrink-0 px-4 pt-2">
            <button type="button" onClick={() => setKDoreseniOpen(true)}
                className="w-full flex items-center gap-2.5 rounded-xl px-3"
                style={{ minHeight: 48, background: `${WARN}14`, border: `1px solid ${WARN}38` }}>
                <ListChecks size={16} style={{ color: WARN, flexShrink: 0 }} />
                <span className="text-[13px] font-semibold flex-1 text-left">K dořešení</span>
                <span className="text-[13px] font-bold tabular-nums" style={{ color: WARN }}>{pocetNalezu}</span>
                <ChevronRight size={16} style={{ color: 'var(--text-tertiary)' }} />
            </button>
        </div>
    );

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {!isMobile && (
                <div className="flex-shrink-0">
                    <ObdobiNavigator
                        mode="mesic" rok={rok} mesic={mesic} setRok={setRok} setMesic={setMesic}
                        mezera={false}
                    >
                        <Dropdown
                            value={ucetId || ''}
                            onChange={v => setUcetId(v || null)}
                            isDark={isDark} accent={ACCENT} height={32} minWidth={260}
                            options={[
                                { value: '', label: 'Všechny účty' },
                                ...ucty.map(u => ({ value: u.id, label: `${u.nazev} · ${u.cislo_uctu}` })),
                            ]}
                        />
                    </ObdobiNavigator>
                    <div className="mt-1.5 mb-3 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                        Pohyby za {MESICE_PLNE[mesic - 1].toLowerCase()} · zůstatek a „K dořešení" vpravo platí pro {ucetLabel}
                    </div>
                </div>
            )}

            {isMobile && (
                <div className="flex-shrink-0 px-4 pt-3">
                    <ObdobiNavigator
                        mode="mesic" rok={rok} mesic={mesic} setRok={setRok} setMesic={setMesic}
                        isMobile mezera={false}
                    >
                        <Dropdown
                            value={ucetId || ''}
                            onChange={v => setUcetId(v || null)}
                            isDark={isDark} accent={ACCENT} height={44}
                            options={[
                                { value: '', label: 'Všechny účty' },
                                ...ucty.map(u => ({ value: u.id, label: `${u.nazev} · ${u.cislo_uctu}` })),
                            ]}
                        />
                    </ObdobiNavigator>
                </div>
            )}

            <input ref={fileRef} type="file" multiple accept=".gpc,.abo,.txt,.sta" className="hidden" onChange={nahrajSoubor} />

            <ModulLayout
                isMobile={isMobile}
                vybrano={!!vybrany}
                panelVzdy
                mobilRezim
                mobilPanel={false}
                barva={ACCENT} isDark={isDark}
                seznam={({ siroky }) => (
                    <>
                        {!isMobile && souhrnImportu}
                        {isMobile && souhrnImportu && <div className="flex-shrink-0 px-4 pt-2">{souhrnImportu}</div>}
                        {kDoreseniRadek}
                        <PohybyListPanel
                            siroky={siroky} mobilni={isMobile}
                            isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle}
                            transakce={transakce} loading={loading} staty={staty}
                            pohled={pohled} onPohled={setPohled}
                            hledani={{ value: hledej, onChange: setHledej }}
                            filtrHodnoty={filtrHodnoty} onFiltr={nastavFiltr} onResetFiltru={resetFiltru}
                            vybranyId={vybranyId} onVyber={setVybranyId}
                            onNahrat={() => fileRef.current?.click()}
                            onProjit={projitPohyby}
                            onPravidla={() => setPravidlaOpen(true)}
                            onSparujKartove={sparujKartove} parujiKarty={parujiKarty}
                            vyberRezim={vyberRezim} vybrane={vybrane} onOznac={oznac}
                            onPrepniRezim={prepniVyberRezim} listaDole={hromadnaLista}
                            nahravam={uploading} projizdim={navrhyBusy}
                            rezervaDole={rezervaDole}
                        />
                    </>
                )}
                detail={() => (
                    <PohybDetail
                        transakce={vybrany} cardStyle={cardStyle} isMobile={isMobile}
                        onZpet={() => setVybranyId(null)}
                        onParovat={() => setParujici(vybrany)}
                        onZaradit={() => setZarazujici(vybrany)}
                        onVytvoritDoklad={() => setDokladProPlatbu(vybrany)}
                        onOdpojit={() => rozparuj(vybrany)}
                        onIgnorovat={() => ignoruj(vybrany)}
                        onOpenDoklad={onOpenDoklad}
                        rezervaDole={rezervaDole}
                    />
                )}
                panel={() => (
                    <BankaStatistikyPanel
                        staty={staty} cardStyle={cardStyle}
                        obdobiLabel={`${MESICE_PLNE[mesic - 1]} ${rok}`}
                        ucetLabel={ucetLabel}
                        onPohled={setPohled}
                    />
                )}
            />

            {parujici && (
                <ParovaciModal
                    transakce={parujici}
                    isDark={isDark}
                    cardStyle={cardStyle}
                    onClose={() => setParujici(null)}
                    onHotovo={(nt) => { patchTransakce(nt); setParujici(null); nactiStatistiky(); }}
                />
            )}
            {hromadneZarazeni && vybrane.size > 0 && (
                <ZaraditModal
                    hromadne={{ pocet: vybrane.size, soucet: soucetVybranych }}
                    cardStyle={cardStyle}
                    onClose={() => setHromadneZarazeni(false)}
                    onZarad={(_, tag, poznamka) => zaraditVybrane(tag, poznamka)}
                />
            )}
            {zarazujici && (
                <ZaraditModal
                    transakce={zarazujici}
                    cardStyle={cardStyle}
                    onClose={() => setZarazujici(null)}
                    onZarad={zarad}
                />
            )}
            {dokladProPlatbu && (
                <VytvorDokladModal
                    transakce={dokladProPlatbu}
                    cardStyle={cardStyle}
                    isDark={isDark}
                    onClose={() => setDokladProPlatbu(null)}
                    onHotovo={(nt) => { patchTransakce(nt); setDokladProPlatbu(null); nactiStatistiky(); }}
                    onOpenDoklad={onOpenDoklad}
                />
            )}
            {navrhy && (
                <NavrhyModal
                    navrhy={navrhy}
                    cardStyle={cardStyle}
                    isDark={isDark}
                    onOpenDoklad={onOpenDoklad}
                    onParovat={(id) => { const t = transakce.find(x => x.id === id); if (t) { setNavrhy(null); setParujici(t); } }}
                    onRefresh={() => { nactiTransakce(); nactiStatistiky(); }}
                    onClose={() => setNavrhy(null)}
                    onHotovo={() => { setNavrhy(null); nactiTransakce(); nactiStatistiky(); }}
                />
            )}
            {pravidlaOpen && (
                <PravidlaModal cardStyle={cardStyle} onClose={() => setPravidlaOpen(false)} />
            )}
            {kDoreseniOpen && (
                <ModalShell open onClose={() => setKDoreseniOpen(false)} isDark={isDark}
                    nadpis="K dořešení" ikona={ListChecks} barva={ACCENT}>
                    <div className="flex flex-col gap-3">
                        <BankaStatistikyPanel
                            staty={staty} cardStyle={cardStyle}
                            obdobiLabel={`${MESICE_PLNE[mesic - 1]} ${rok}`}
                            ucetLabel={ucetLabel}
                            onPohled={(p) => { setPohled(p); setKDoreseniOpen(false); }}
                        />
                    </div>
                </ModalShell>
            )}
        </div>
    );
}

/**
 * Modal „Projít pohyby" — AI/pravidlové návrhy k potvrzení. Dva druhy řádků:
 *  - `pair` = AI navrhuje KONKRÉTNÍ doklad → tlačítko „Spárovat" (přímo spáruje),
 *    „Jiný…" otevře ruční párování;
 *  - `classify` = kategorie (editovatelná) + zaškrtnutí → „Přijmout vybrané".
 * AI se řídí editovatelnými pravidly (tlačítko „AI pravidla" v bance). Nic se
 * nezmění bez potvrzení.
 */
function NavrhyModal({ navrhy, cardStyle, isDark, onParovat, onOpenDoklad, onClose, onHotovo, onRefresh }) {
    const surf = tableSurfaces(isDark);
    // classify řádky: { [id]: { tag, vybrano } }
    const [stav, setStav] = useState(() => Object.fromEntries(
        navrhy.items.filter(n => n.action !== 'pair')
            .map(n => [n.transakce_id, { tag: n.tag, vybrano: n.tag !== 'ma_doklad' && n.confidence >= 0.6 }])
    ));
    const [hotove, setHotove] = useState({}); // { [id]: true } — už spárované v tomto okně
    const [parujiId, setParujiId] = useState(null);
    const [ukladam, setUkladam] = useState(false);

    const set = (id, patch) => setStav(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    const vybrane = navrhy.items.filter(n => n.action !== 'pair'
        && !hotove[n.transakce_id] && stav[n.transakce_id]?.vybrano && stav[n.transakce_id]?.tag && stav[n.transakce_id]?.tag !== 'ma_doklad');

    const prijmi = async () => {
        setUkladam(true);
        const polozky = vybrane.map(n => ({ transakce_id: n.transakce_id, tag: stav[n.transakce_id].tag }));
        const r = await postJson('/api/finance/banka/zaradit-hromadne', { polozky });
        setUkladam(false);
        if (r.ok) onHotovo();
    };

    const sparuj = async (n) => {
        if (!n.doklad) return;
        setParujiId(n.transakce_id);
        const r = await postJson(`/api/finance/banka/transakce/${n.transakce_id}/parovat`, {
            alokace: [{ doklad_id: n.doklad.id, castka: n.pohyb.castka }],
            oznacit_zaplaceno: true,
        });
        setParujiId(null);
        if (r.ok) { setHotove(h => ({ ...h, [n.transakce_id]: true })); onRefresh?.(); }
        else { const j = await r.json().catch(() => ({})); alert('Párování selhalo: ' + (j.error || '')); }
    };

    const RadekPohyb = ({ n }) => (
        <div className="flex-1 min-w-0">
            <div className="text-sm truncate" style={{ color: 'var(--text)' }}>
                <Castka smer={n.pohyb.smer} castka={n.pohyb.castka} size="0.9rem" /> · {n.pohyb.protistrana_nazev || n.pohyb.popis}
            </div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {fmtDate(n.pohyb.datum)}
                {n.pohyb.ucet_nazev ? ` · ${n.pohyb.ucet_nazev}` : ''}
                {n.pohyb.karta_posledni4 ? ` · karta •${n.pohyb.karta_posledni4}` : ''}
                {n.pohyb.variabilni_symbol ? ` · VS ${n.pohyb.variabilni_symbol}` : ''}
            </div>
        </div>
    );
    const ZdrojBadge = ({ n }) => (
        <span className="text-xs rounded px-1.5 py-0.5 whitespace-nowrap" style={{ background: surf.headBg, color: n.zdroj === 'ai' ? ACCENT : 'var(--text-muted)' }}>
            {n.zdroj === 'ai' ? 'AI' : 'pravidlo'}{n.confidence != null ? ` ${Math.round(n.confidence * 100)}%` : ''}
        </span>
    );

    const parove = navrhy.items.filter(n => n.action === 'pair');
    const klasifikace = navrhy.items.filter(n => n.action !== 'pair');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-auto p-4" style={cardStyle} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
                        <Sparkles size={16} style={{ color: ACCENT }} /> Návrhy párování a zařazení
                    </div>
                    <button onClick={onClose}><X size={18} style={{ color: 'var(--text-muted)' }} /></button>
                </div>
                <div className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                    {navrhy.items.length} návrhů z {navrhy.celkem} nespárovaných{navrhy.ai_pouzito ? ' · použita i AI (Haiku)' : ' · jen pravidla'}
                </div>

                {navrhy.items.length === 0 ? (
                    <div className="text-center py-10 text-sm" style={{ color: 'var(--text-muted)' }}>Žádné návrhy — vše je vyřešené nebo je potřeba ruční posouzení.</div>
                ) : (
                    <div className="space-y-3">
                        {/* Návrhy párování na konkrétní doklad */}
                        {parove.length > 0 && (
                            <div>
                                <div className="text-xs uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Párování na doklad ({parove.length})</div>
                                <div className="space-y-1">
                                    {parove.map(n => {
                                        const done = hotove[n.transakce_id];
                                        return (
                                            <div key={n.transakce_id} className="rounded-lg px-2 py-1.5" style={{ background: 'var(--bg-elevated)', border: `1px solid ${done ? OK : ACCENT}` }}>
                                                <div className="flex items-center gap-2">
                                                    <RadekPohyb n={n} />
                                                    <ZdrojBadge n={n} />
                                                </div>
                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→ doklad:</span>
                                                    <button onClick={() => onOpenDoklad?.(n.doklad.id)} className="text-xs inline-flex items-center gap-1" style={{ color: ACCENT }}>
                                                        <FileText size={11} /> {n.doklad.interni_cislo || `#${n.doklad.id}`} · {n.doklad.dodavatel || 'bez dodavatele'} · {fmt(n.doklad.castka_s_dph)} Kč
                                                    </button>
                                                    {n.duvod && <span className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>· {n.duvod}</span>}
                                                    <div className="ml-auto flex items-center gap-2">
                                                        {done ? (
                                                            <span className="text-xs inline-flex items-center gap-1" style={{ color: OK }}><Check size={13} /> Spárováno</span>
                                                        ) : (
                                                            <>
                                                                <button onClick={() => onParovat(n.transakce_id)} className="text-xs" style={{ color: 'var(--text-muted)' }}>Jiný…</button>
                                                                <button onClick={() => sparuj(n)} disabled={parujiId === n.transakce_id}
                                                                    className="text-xs font-medium inline-flex items-center gap-1 px-2 py-1 rounded-lg"
                                                                    style={{ background: OK, color: '#fff', opacity: parujiId === n.transakce_id ? 0.6 : 1 }}>
                                                                    {parujiId === n.transakce_id ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Spárovat
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Návrhy zařazení do kategorie (bez dokladu) */}
                        {klasifikace.length > 0 && (
                            <div>
                                <div className="text-xs uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Zařazení bez dokladu ({klasifikace.length})</div>
                                <div className="space-y-1">
                                    {klasifikace.map(n => {
                                        const s = stav[n.transakce_id];
                                        return (
                                            <div key={n.transakce_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                                                <input type="checkbox" checked={!!s?.vybrano} onChange={e => set(n.transakce_id, { vybrano: e.target.checked })} />
                                                <RadekPohyb n={n} />
                                                <ZdrojBadge n={n} />
                                                <div style={{ minWidth: 190 }}>
                                                    <Dropdown value={s?.tag} onChange={v => set(n.transakce_id, { tag: v })}
                                                        isDark={isDark} height={30} placeholder="— zařadit —"
                                                        options={KATEGORIE.map(k => ({ value: k.tag, label: k.label }))} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Zavřít</button>
                    <button onClick={prijmi} disabled={ukladam || vybrane.length === 0}
                        className="text-sm font-medium px-4 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                        style={{ background: ACCENT, color: '#fff', opacity: (ukladam || vybrane.length === 0) ? 0.5 : 1 }}>
                        {ukladam ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Přijmout vybrané ({vybrane.length})
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Editor „AI pravidla" — volný text, kterým uživatel učí AI provozní kontext
 * (které karty jsou osobní, co znamená příchozí platba na hlavní vs. provozní
 * účet apod.). Ukládá se do `cis_bank_ai_pravidla` a vkládá do promptu.
 */
function PravidlaModal({ cardStyle, onClose }) {
    const [pravidla, setPravidla] = useState('');
    const [loading, setLoading] = useState(true);
    const [ukladam, setUkladam] = useState(false);
    const [ulozeno, setUlozeno] = useState(false);

    useEffect(() => {
        fetch('/api/finance/banka/ai-pravidla', { credentials: 'same-origin' })
            .then(r => r.json()).then(d => setPravidla(d?.pravidla || '')).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const uloz = async () => {
        setUkladam(true); setUlozeno(false);
        try {
            const r = await fetch('/api/finance/banka/ai-pravidla', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token(), 'Accept': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ pravidla }),
            });
            if (!r.ok) throw new Error(await r.text());
            setUlozeno(true);
        } catch (e) { alert('Uložení selhalo: ' + e.message); }
        setUkladam(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="rounded-2xl w-full max-w-2xl p-4" style={cardStyle} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
                        <Wand2 size={16} style={{ color: ACCENT }} /> AI pravidla banky (učení)
                    </div>
                    <button onClick={onClose}><X size={18} style={{ color: 'var(--text-muted)' }} /></button>
                </div>
                <div className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                    Piš pravidla přirozeným jazykem — AI se jimi řídí při návrzích párování a zařazení pohybů.
                    Např. „karta 4356 = osobní karta k hlavnímu účtu, patří na doklad", „příchozí na provozní účet = převod", „příchozí na hlavní účet = tržba".
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin" style={{ color: ACCENT }} /></div>
                ) : (
                    <textarea value={pravidla} onChange={e => { setPravidla(e.target.value); setUlozeno(false); }} rows={12}
                        placeholder="- pravidlo 1&#10;- pravidlo 2"
                        className="w-full text-sm rounded-lg px-3 py-2 font-mono"
                        style={{ background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)', resize: 'vertical' }} />
                )}
                <div className="flex items-center justify-end gap-2 mt-3">
                    {ulozeno && <span className="text-xs mr-auto inline-flex items-center gap-1" style={{ color: OK }}><Check size={13} /> Uloženo</span>}
                    <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Zavřít</button>
                    <button onClick={uloz} disabled={ukladam || loading}
                        className="text-sm font-medium px-4 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                        style={{ background: ACCENT, color: '#fff', opacity: (ukladam || loading) ? 0.5 : 1 }}>
                        {ukladam ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Uložit pravidla
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Modal pro zařazení platby BEZ dokladu na kategorii (odvody, DPH, splátky…).
 * S `hromadne` ({pocet, soucet}) místo `transakce` zařazuje celý výběr —
 * poznámka se pak zapíše jen, když ji vyplníš (prázdná ty stávající nemaže).
 */
function ZaraditModal({ transakce = null, hromadne = null, cardStyle, onClose, onZarad }) {
    const [tag, setTag] = useState(transakce?.tag || '');
    const [poznamka, setPoznamka] = useState(transakce?.poznamka || '');
    const [ukladam, setUkladam] = useState(false);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="rounded-2xl w-full max-w-md p-4" style={cardStyle} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                        {hromadne ? `Zařadit ${hromadne.pocet} plateb (bez dokladu)` : 'Zařadit platbu (bez dokladu)'}
                    </div>
                    <button onClick={onClose}><X size={18} style={{ color: 'var(--text-muted)' }} /></button>
                </div>
                <div className="text-xs mb-3 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                    {hromadne
                        ? <>Vybráno {hromadne.pocet} pohybů · součet <Castka smer={hromadne.soucet >= 0 ? 'prichozi' : 'odchozi'} castka={Math.abs(hromadne.soucet)} size="0.95rem" /> Kč</>
                        : <>{fmtDate(transakce.datum)} · <Castka smer={transakce.smer} castka={transakce.castka} size="0.95rem" /> Kč · {zobrazProtistranu(transakce)}</>}
                </div>
                <div className="grid grid-cols-1 gap-1.5 mb-3">
                    {KATEGORIE.map(k => (
                        <button key={k.tag} onClick={() => setTag(k.tag)}
                            className="text-left text-sm rounded-lg px-3 py-2"
                            style={tag === k.tag
                                ? { background: ACCENT, color: '#fff' }
                                : { background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)' }}>
                            {k.label}
                        </button>
                    ))}
                </div>
                <input value={poznamka} onChange={e => setPoznamka(e.target.value)} placeholder="Poznámka (volitelné)"
                    className="text-sm rounded-lg px-3 py-2 w-full mb-3"
                    style={{ background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)' }} />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Zrušit</button>
                    <button disabled={!tag || ukladam} onClick={async () => { setUkladam(true); await onZarad(transakce, tag, poznamka); setUkladam(false); }}
                        className="text-sm font-medium px-4 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                        style={{ background: ACCENT, color: '#fff', opacity: (!tag || ukladam) ? 0.5 : 1 }}>
                        {ukladam ? <Loader2 size={14} className="animate-spin" /> : <Tag size={14} />} Zařadit
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Rychlý doklad k platbě bez dokladu — předvyplní z pohybu, vytvoří + spáruje. */
function VytvorDokladModal({ transakce, cardStyle, isDark, onClose, onHotovo }) {
    const cardMask = maskaKarty(transakce.protistrana_nazev);
    const [dodavatelId, setDodavatelId] = useState('');
    const [dodavatele, setDodavatele] = useState([]);
    const [kategorieId, setKategorieId] = useState('');
    const [dphSazba, setDphSazba] = useState(21);
    const [popis, setPopis] = useState('');
    const [kategorie, setKategorie] = useState([]);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);

    useEffect(() => {
        fetch('/api/finance/kategorie').then(r => r.json())
            .then(d => setKategorie((Array.isArray(d) ? d : (d?.kategorie || [])).filter(k => k.skupina !== 'trzby')))
            .catch(() => {});
        fetch('/api/dodavatele').then(r => r.json())
            .then(d => {
                const list = (Array.isArray(d) ? d : (d?.data || d?.dodavatele || []))
                    .slice().sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs'));
                setDodavatele(list);
                // Předvyplnění: zkus najít dodavatele podle názvu protistrany (ne u karet).
                if (!cardMask && transakce.protistrana_nazev) {
                    const q = transakce.protistrana_nazev.toLowerCase();
                    const m = list.find(x => x.nazev && (q.includes(x.nazev.toLowerCase()) || x.nazev.toLowerCase().includes(q)));
                    if (m) setDodavatelId(String(m.id));
                }
            })
            .catch(() => {});
    }, []);

    const inp = { background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)' };
    const uloz = async () => {
        if (!kategorieId) { setChyba('Vyber P&L kategorii'); return; }
        setUkladam(true); setChyba(null);
        const r = await postJson(`/api/finance/banka/transakce/${transakce.id}/vytvor-doklad`, {
            dodavatel_id: dodavatelId ? Number(dodavatelId) : null, kategorie_id: Number(kategorieId), dph_sazba: Number(dphSazba), popis: popis || null,
        });
        const j = await r.json().catch(() => ({}));
        setUkladam(false);
        if (!r.ok) { setChyba(j.error || j.message || 'Vytvoření selhalo'); return; }
        onHotovo(j.transakce);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="rounded-2xl w-full max-w-md p-4" style={cardStyle} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Vytvořit doklad k platbě</div>
                    <button onClick={onClose}><X size={18} style={{ color: 'var(--text-muted)' }} /></button>
                </div>
                <div className="text-xs mb-3 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                    {fmtDate(transakce.datum)} · <Castka smer={transakce.smer} castka={transakce.castka} size="0.95rem" /> Kč{cardMask ? ` · Platba kartou •••${cardMask[1]}` : ''}
                </div>
                <div className="space-y-2 mb-3">
                    <div>
                        <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Dodavatel</label>
                        <Dropdown value={dodavatelId} onChange={setDodavatelId} isDark={isDark}
                            placeholder="— vyber dodavatele —"
                            options={dodavatele.map(d => ({ value: String(d.id), label: `${d.nazev}${d.ico ? ` (${d.ico})` : ''}` }))} />
                        <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                            Není v seznamu? Založ ho v Dokladech → dodavatelé (i zahraniční).
                        </div>
                    </div>
                    <div>
                        <label className="text-xs" style={{ color: 'var(--text-muted)' }}>P&L kategorie</label>
                        <Dropdown value={kategorieId} onChange={setKategorieId} isDark={isDark}
                            placeholder="— vyber kategorii —"
                            options={kategorie.map(k => ({ value: String(k.id), label: k.nazev }))} />
                    </div>
                    <div>
                        <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Sazba DPH</label>
                        <Dropdown value={dphSazba} onChange={setDphSazba} isDark={isDark}
                            options={[
                                { value: 21, label: '21 %' },
                                { value: 12, label: '12 %' },
                                { value: 0, label: '0 % / bez DPH' },
                            ]} />
                    </div>
                    <div>
                        <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Popis (volitelné)</label>
                        <input value={popis} onChange={e => setPopis(e.target.value)} placeholder="Popis položky"
                            className="text-sm rounded-lg px-3 py-2 w-full" style={inp} />
                    </div>
                </div>
                {chyba && <div className="text-xs mb-2" style={{ color: BAD }}>{chyba}</div>}
                <div className="text-[11px] mb-3" style={{ color: 'var(--text-muted)' }}>
                    Vytvoří schválený doklad na {fmt(transakce.castka)} Kč a rovnou ho spáruje s touto platbou.
                </div>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Zrušit</button>
                    <button disabled={!kategorieId || ukladam} onClick={uloz}
                        className="text-sm font-medium px-4 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                        style={{ background: ACCENT, color: '#fff', opacity: (!kategorieId || ukladam) ? 0.5 : 1 }}>
                        {ukladam ? <Loader2 size={14} className="animate-spin" /> : <FilePlus size={14} />} Vytvořit a spárovat
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Modal pro ruční (i vícedokladové) párování: návrhy + hledání + alokace. */
function ParovaciModal({ transakce, isDark, cardStyle, onClose, onHotovo }) {
    const [navrhy, setNavrhy] = useState([]);
    const [hledej, setHledej] = useState('');
    const [nalezene, setNalezene] = useState([]);
    const [vybrane, setVybrane] = useState({}); // { doklad_id: { doklad, castka } }
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [oznacitZaplaceno, setOznacitZaplaceno] = useState(true); // dotaz: označit doklad jako zaplacený?
    const surf = tableSurfaces(isDark);

    useEffect(() => {
        fetch(`/api/finance/banka/transakce/${transakce.id}/navrhy`).then(r => r.json()).then(d => setNavrhy(Array.isArray(d) ? d : [])).catch(() => {});
    }, [transakce.id]);
    useEffect(() => {
        if (!hledej) { setNalezene([]); return; }
        const id = setTimeout(() => {
            fetch('/api/finance/banka/doklady?q=' + encodeURIComponent(hledej)).then(r => r.json()).then(d => setNalezene(Array.isArray(d) ? d : [])).catch(() => {});
        }, 300);
        return () => clearTimeout(id);
    }, [hledej]);

    const pridej = (d) => setVybrane(prev => prev[d.id] ? prev : { ...prev, [d.id]: { doklad: d, castka: d.castka_s_dph } });
    const odeber = (id) => setVybrane(prev => { const n = { ...prev }; delete n[id]; return n; });
    const nastavCastku = (id, v) => setVybrane(prev => ({ ...prev, [id]: { ...prev[id], castka: v } }));

    const seznamVybranych = Object.values(vybrane);
    const sumaAlokace = seznamVybranych.reduce((s, x) => s + (parseFloat(x.castka) || 0), 0);
    // Tolerance zaokrouhlení (~1 Kč/doklad) — MAKRO ap. má zaokrouhlený doklad,
    // kartou se strhne přesná částka. Malý rozdíl párování povolí.
    const tolerance = Math.max(0.01, seznamVybranych.length * 1.0);
    const rozdil = sumaAlokace - transakce.castka;
    const prekroceno = rozdil > tolerance;
    const zaokrouhleni = !prekroceno && Math.abs(rozdil) > 0.01; // drobný rozdíl = zaokrouhlení

    const potvrd = async () => {
        setUkladam(true); setChyba(null);
        const alokace = seznamVybranych.map(x => ({ doklad_id: x.doklad.id, castka: parseFloat(x.castka) || 0 }));
        const r = await postJson(`/api/finance/banka/transakce/${transakce.id}/parovat`, { alokace, oznacit_zaplaceno: oznacitZaplaceno });
        const j = await r.json();
        setUkladam(false);
        if (!r.ok) { setChyba(j.error || 'Párování selhalo'); return; }
        onHotovo(j.transakce);
    };

    const RadekKandidat = ({ d, duvody }) => (
        <button onClick={() => pridej(d)} disabled={!!vybrane[d.id]}
            className="w-full flex items-center justify-between text-left px-3 py-2 rounded-lg mb-1"
            style={{
                background: 'var(--bg-elevated)',
                border: d.kartovy ? `1px solid ${ACCENT}` : '1px solid var(--border)',
                opacity: vybrane[d.id] ? 0.5 : 1,
            }}>
            <div>
                <div className="text-sm flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
                    {d.kartovy && <CreditCard size={13} style={{ color: ACCENT }} />}
                    {d.interni_cislo || `#${d.id}`} · {d.dodavatel || 'bez dodavatele'}
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {fmt(d.castka_s_dph)} Kč{d.variabilni_symbol ? ` · VS ${d.variabilni_symbol}` : ''} · {d.stav}
                    {d.datum ? ` · zaúčtováno ${fmtDate(d.datum)}` : ''}
                    {d.kartovy && d.flek_karta ? ` · karta •••${d.flek_karta}${d.flek ? ` (${d.flek})` : ''}` : ''}
                    {duvody?.length ? ` · shoda: ${duvody.join(', ')}` : ''}
                </div>
            </div>
            <span className="text-xs font-medium whitespace-nowrap ml-2" style={{ color: ACCENT }}>
                {d.kartovy ? 'návrh kartou +' : '+ přidat'}
            </span>
        </button>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-auto p-4" style={cardStyle} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Párovat pohyb</div>
                        <div className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                            {fmtDate(transakce.datum)} · <Castka smer={transakce.smer} castka={transakce.castka} size="0.95rem" /> Kč
                            · {zobrazProtistranu(transakce)}
                        </div>
                    </div>
                    <button onClick={onClose}><X size={18} style={{ color: 'var(--text-muted)' }} /></button>
                </div>

                {/* Vybrané doklady + alokace */}
                {seznamVybranych.length > 0 && (
                    <div className="mb-3 rounded-lg p-2" style={{ background: surf.headBg }}>
                        <div className="text-xs uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Vybráno ({seznamVybranych.length})</div>
                        {seznamVybranych.map(({ doklad, castka }) => (
                            <div key={doklad.id} className="flex items-center gap-2 mb-1">
                                <FileText size={13} style={{ color: ACCENT }} />
                                <span className="text-sm flex-1" style={{ color: 'var(--text)' }}>{doklad.interni_cislo || `#${doklad.id}`} · {doklad.dodavatel}</span>
                                <input type="number" step="0.01" value={castka} onChange={e => nastavCastku(doklad.id, e.target.value)}
                                    className="text-sm text-right rounded px-2 py-1 w-28"
                                    style={{ background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)' }} />
                                <button onClick={() => odeber(doklad.id)}><X size={14} style={{ color: BAD }} /></button>
                            </div>
                        ))}
                        <div className="text-xs mt-1 text-right" style={{ color: prekroceno ? BAD : (zaokrouhleni ? OK : 'var(--text-muted)') }}>
                            Alokováno {fmt(sumaAlokace)} / {fmt(transakce.castka)} Kč{prekroceno ? ' — překročeno!' : (zaokrouhleni ? ' — rozdíl zaokrouhlením ✓' : '')}
                        </div>
                    </div>
                )}

                {/* Návrhy */}
                {navrhy.length > 0 && (
                    <div className="mb-3">
                        <div className="text-xs uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Návrhy</div>
                        {navrhy.map(d => <RadekKandidat key={d.id} d={d} duvody={d.duvody} />)}
                    </div>
                )}

                {/* Vyhledávání dokladů */}
                <div className="relative mb-2">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                    <input value={hledej} onChange={e => setHledej(e.target.value)} placeholder="Najít doklad (číslo, dodavatel, VS)…"
                        className="text-sm rounded-lg pl-7 pr-2 py-1.5 w-full"
                        style={{ background: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border)' }} />
                </div>
                {nalezene.map(d => <RadekKandidat key={d.id} d={d} />)}

                {chyba && <div className="text-sm mt-2" style={{ color: BAD }}><AlertTriangle size={14} className="inline mr-1" />{chyba}</div>}

                {/* Dotaz: má se doklad označit jako zaplacený? (karta/převod jinak zůstávají „čeká na platbu") */}
                <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" style={{ color: 'var(--text)' }}>
                    <input type="checkbox" checked={oznacitZaplaceno} onChange={e => setOznacitZaplaceno(e.target.checked)} />
                    Označit doklad(y) jako <b>zaplacené</b>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>(jinak jen napáruje a nechá „čeká na platbu")</span>
                </label>

                <div className="flex justify-end gap-2 mt-3">
                    <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Zrušit</button>
                    <button onClick={potvrd} disabled={ukladam || seznamVybranych.length === 0 || prekroceno}
                        className="text-sm font-medium px-4 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                        style={{ background: OK, color: '#fff', opacity: (ukladam || seznamVybranych.length === 0 || prekroceno) ? 0.5 : 1 }}>
                        {ukladam ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {oznacitZaplaceno ? 'Spárovat a zaplatit' : 'Jen spárovat'}
                    </button>
                </div>
            </div>
        </div>
    );
}
